import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
  Stack,
  TextField,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  RocketLaunch as RocketIcon,
  VpnKey as KeyIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useSubscription } from '../contexts/SubscriptionContext';
import { CopilotApiError } from '../services/copilotApi';

type Mode = 'choose' | 'access-code' | 'trial';

function serverMessage(err: unknown, fallback: string): string {
  if (err instanceof CopilotApiError) {
    const response = err.response as { error?: string } | undefined;
    return response?.error || err.message || fallback;
  }
  return fallback;
}

export const SubscriptionChoice: React.FC = () => {
  const { redeemAccessCode, startCheckout } = useSubscription();
  const [mode, setMode] = useState<Mode>('choose');
  const [code, setCode] = useState('');
  const [acknowledgedAi, setAcknowledgedAi] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetTo = (next: Mode): void => {
    setError(null);
    setMode(next);
  };

  const handleRedeem = async (): Promise<void> => {
    if (!code.trim()) {
      setError('Please enter your access code.');
      return;
    }
    try {
      setBusy(true);
      setError(null);
      await redeemAccessCode(code.trim());
      // Subscription context updates and renders the app automatically.
    } catch (err) {
      setError(serverMessage(err, 'Could not validate that access code. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const handleStartTrial = async (): Promise<void> => {
    try {
      setBusy(true);
      setError(null);
      await startCheckout();
      // Browser redirects to Stripe Checkout on success.
    } catch (err) {
      setError(serverMessage(err, 'Could not start your free trial. Please try again.'));
      setBusy(false);
    }
  };

  return (
    <Box sx={{
      height: '100vh',
      width: '100vw',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
      boxSizing: 'border-box',
      overflow: 'hidden',
    }}>
      <Paper
        elevation={8}
        sx={{
          p: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid',
          borderColor: 'divider',
          maxWidth: 640,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxSizing: 'border-box',
        }}
      >
        <Stack spacing={3} alignItems="center">
          {/* Logo */}
          <Box
            component="img"
            src={new URL('../assets/gekkovet-logo.png', import.meta.url).href}
            alt="GekkoVet Logo"
            sx={{ height: 72, width: 'auto', objectFit: 'contain' }}
          />

          <Box textAlign="center">
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="primary">
              Welcome to GekkoVet Copilot
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Choose how you&apos;d like to get started.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          )}

          {/* ---- Choose ---- */}
          {mode === 'choose' && (
            <Stack spacing={2} sx={{ width: '100%' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<RocketIcon />}
                onClick={() => resetTo('trial')}
                sx={{ py: 1.5, fontSize: '1.05rem', fontWeight: 600, borderRadius: 2 }}
              >
                Start your 14-day free trial
              </Button>
              <Typography variant="caption" color="text.secondary" textAlign="center">
                No access code? Try GekkoVet free for 14 days.
              </Typography>

              <Divider>or</Divider>

              <Button
                variant="outlined"
                size="large"
                startIcon={<KeyIcon />}
                onClick={() => resetTo('access-code')}
                sx={{ py: 1.5, fontSize: '1.05rem', fontWeight: 600, borderRadius: 2 }}
              >
                I have an access code
              </Button>
            </Stack>
          )}

          {/* ---- Access code ---- */}
          {mode === 'access-code' && (
            <Stack spacing={2} sx={{ width: '100%' }}>
              <Typography variant="h6" fontWeight={600}>
                Enter your access code
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter the access code provided to you. It unlocks GekkoVet at no charge for its
                validity period.
              </Typography>
              <TextField
                label="Access code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                fullWidth
                autoFocus
                disabled={busy}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleRedeem();
                }}
              />
              <Button
                variant="contained"
                size="large"
                onClick={handleRedeem}
                disabled={busy || !code.trim()}
                startIcon={busy ? <CircularProgress size={20} color="inherit" /> : <KeyIcon />}
                sx={{ py: 1.5, fontWeight: 600, borderRadius: 2 }}
              >
                {busy ? 'Validating…' : 'Unlock access'}
              </Button>
              <Button
                variant="text"
                startIcon={<BackIcon />}
                onClick={() => resetTo('choose')}
                disabled={busy}
              >
                Back
              </Button>
            </Stack>
          )}

          {/* ---- Trial ---- */}
          {mode === 'trial' && (
            <Stack spacing={2} sx={{ width: '100%' }}>
              <Typography variant="h6" fontWeight={600}>
                Start your 14-day free trial
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You&apos;ll get full access to GekkoVet Copilot for 14 days. A credit card is
                required to start.
              </Typography>

              <Alert severity="info">
                After your 14-day trial you&apos;ll be charged <strong>€49/month for Starter</strong> unless
                you change your plan or cancel. You can switch plans or cancel anytime from billing.
              </Alert>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={acknowledgedAi}
                    onChange={(e) => setAcknowledgedAi(Boolean(e.target.checked))}
                    color="primary"
                    disabled={busy}
                  />
                }
                label={
                  <Typography variant="body2">
                    I understand that this is an AI product that may make mistakes, and I will use my
                    professional judgment when applying any recommendations.
                  </Typography>
                }
              />

              <Button
                variant="contained"
                size="large"
                onClick={handleStartTrial}
                disabled={busy || !acknowledgedAi}
                startIcon={busy ? <CircularProgress size={20} color="inherit" /> : <RocketIcon />}
                sx={{ py: 1.5, fontWeight: 600, borderRadius: 2 }}
              >
                {busy ? 'Redirecting to checkout…' : 'Continue to payment'}
              </Button>
              <Button
                variant="text"
                startIcon={<BackIcon />}
                onClick={() => resetTo('choose')}
                disabled={busy}
              >
                Back
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};
